'use client';

import React from 'react';
import Link from '@/components/atoms/link';
import { Bars3Icon } from '@heroicons/react/24/outline';
import type { NavItem } from '../../types';

interface DesktopNavProps {
  mainNavItems: NavItem[];
  /** Right-aligned CTA slot (e.g. the guest-only Register Trade Account trigger). */
  cta?: React.ReactNode;
  // Toggles the products dialog open/closed. A disclosure trigger that
  // advertises `aria-expanded` must collapse on re-click, so this is a
  // toggle rather than open-only.
  onProductsMenuToggle: () => void;
  // Disclosure state + the id of the products dialog panel, so the
  // trigger exposes an accessible open/closed relationship to the menu.
  productsMenuOpen: boolean;
  productsMenuId: string;
}

const DesktopNav = ({ mainNavItems, cta, onProductsMenuToggle, productsMenuOpen, productsMenuId }: DesktopNavProps) => {
  const [shopProductsItem, ...rest] = mainNavItems;

  return (
    <nav className="hidden border-b border-gray-200 bg-white lg:block">
      <div className="flex h-12 items-center justify-between pr-0 pl-8">
        <div className="flex items-center">
          {shopProductsItem && (
            <button
              onClick={onProductsMenuToggle}
              // The Drawer closes on any outside-click via a document-level
              // `mouseup` listener, and this trigger lives outside the panel.
              // Without this, clicking the trigger while open would fire that
              // listener (close) and then this onClick (toggle back open) —
              // the button would appear dead. Stopping the mouseup here keeps
              // the trigger's own click from reaching the outside-click
              // detector, so the toggle is the single source of truth.
              onMouseUp={(e) => e.stopPropagation()}
              aria-haspopup="dialog"
              aria-controls={productsMenuId}
              aria-expanded={productsMenuOpen}
              className="text-16 hover:text-primary flex items-center gap-2 border-r border-gray-300 pr-6 font-medium text-gray-700"
            >
              <Bars3Icon className="size-5" aria-hidden />
              <span>{shopProductsItem.label}</span>
            </button>
          )}

          <div className="flex items-center gap-6 pl-6">
            {rest.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                underlineOnHover={false}
                className="text-16 hover:text-primary font-medium text-gray-700"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>

        {cta}
      </div>
    </nav>
  );
};

export default DesktopNav;
